import { useState } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import {
  Plus, Edit2, Trash2, Calendar, Clock, CheckSquare, Save, MoreVertical, Users, TrendingUp
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export const Tasks = ({ tasks, contacts, deals, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [formData, setFormData] = useState({
    title: "", description: "", due_date: "", status: "todo", priority: "medium", contact_id: "none", deal_id: "none"
  });

  const filteredTasks = tasks.filter(t => filterStatus === "all" || t.status === filterStatus);

  const handleOpenDialog = (task = null) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        title: task.title,
        description: task.description || "",
        due_date: task.due_date ? new Date(task.due_date).toISOString().split('T')[0] : "",
        status: task.status,
        priority: task.priority,
        contact_id: task.contact_id || "none",
        deal_id: task.deal_id || "none"
      });
    } else {
      setEditingTask(null);
      setFormData({ title: "", description: "", due_date: "", status: "todo", priority: "medium", contact_id: "none", deal_id: "none" });
    }
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    try {
      const payload = { ...formData };
      if (payload.due_date) {
        payload.due_date = new Date(payload.due_date).toISOString();
      } else {
        delete payload.due_date;
      }
      if (!payload.contact_id || payload.contact_id === "none") delete payload.contact_id;
      if (!payload.deal_id || payload.deal_id === "none") delete payload.deal_id;

      if (editingTask) {
        await axios.put(`${API}/tasks/${editingTask.id}`, payload);
      } else {
        await axios.post(`${API}/tasks`, payload);
      }
      setDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving task:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this task?")) {
      try {
        await axios.delete(`${API}/tasks/${id}`);
        onRefresh();
      } catch (error) {
        console.error("Error deleting task:", error);
      }
    }
  };

  const handleQuickStatusChange = async (task, newStatus) => {
    try {
      await axios.put(`${API}/tasks/${task.id}`, { status: newStatus });
      onRefresh();
    } catch (error) {
      console.error("Error updating task status:", error);
    }
  };

  const getContactName = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    return contact ? contact.name : '';
  };

  const getDealTitle = (dealId) => {
    const deal = deals.find(d => d.id === dealId);
    return deal ? deal.title : '';
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="tasks-view">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-cabinet text-3xl font-bold text-slate-900">Tasks</h2>
          <p className="text-slate-500 font-manrope">Track and manage your tasks</p>
        </div>
        <Button 
          data-testid="add-task-btn"
          onClick={() => handleOpenDialog()} 
          className="bg-royal-blue-700 hover:bg-royal-blue-800"
        >
          <Plus size={18} className="mr-2" /> Add Task
        </Button>
      </div>

      <div className="flex items-center gap-4">
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="task-status-filter">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tasks</SelectItem>
            <SelectItem value="todo">To Do</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="cancelled">Cancelled</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="grid gap-4">
        {filteredTasks.map((task) => (
          <Card key={task.id} className="border-slate-200 card-hover">
            <CardContent className="p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="flex items-start gap-4 flex-1">
                  <div className={`w-4 h-4 mt-1 rounded-full flex-shrink-0 ${
                    task.priority === 'urgent' ? 'bg-red-500' :
                    task.priority === 'high' ? 'bg-orange-500' :
                    task.priority === 'medium' ? 'bg-gold-500' : 'bg-green-500'
                  }`} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-semibold text-slate-900">{task.title}</h3>
                      <Badge className={`status-${task.status}`}>
                        {task.status.replace('_', ' ')}
                      </Badge>
                      <Badge className={`priority-${task.priority}`}>
                        {task.priority}
                      </Badge>
                    </div>
                    {task.description && (
                      <p className="text-sm text-slate-600 mb-2">{task.description}</p>
                    )}
                    <div className="flex flex-wrap gap-4 text-sm text-slate-500">
                      {task.due_date && (
                        <div className="flex items-center gap-1">
                          <Calendar size={14} />
                          {new Date(task.due_date).toLocaleDateString()}
                        </div>
                      )}
                      {task.contact_id && (
                        <div className="flex items-center gap-1">
                          <Users size={14} />
                          {getContactName(task.contact_id)}
                        </div>
                      )}
                      {task.deal_id && (
                        <div className="flex items-center gap-1">
                          <TrendingUp size={14} />
                          {getDealTitle(task.deal_id)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {/* EDIT BUTTON - This fixes the reported bug */}
                  <Button 
                    data-testid={`edit-task-btn-${task.id}`}
                    variant="outline"
                    size="sm"
                    onClick={() => handleOpenDialog(task)}
                    className="text-royal-blue-700 border-royal-blue-200 hover:bg-royal-blue-50"
                  >
                    <Edit2 size={16} className="mr-1" /> Edit
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`task-menu-${task.id}`}>
                        <MoreVertical size={16} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => handleQuickStatusChange(task, 'todo')}>
                        Mark as To Do
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleQuickStatusChange(task, 'in_progress')}>
                        Mark as In Progress
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleQuickStatusChange(task, 'completed')}>
                        Mark as Completed
                      </DropdownMenuItem>
                      <DropdownMenuItem 
                        onClick={() => handleDelete(task.id)}
                        className="text-red-600"
                      >
                        Delete Task
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
        {filteredTasks.length === 0 && (
          <Card className="border-slate-200">
            <CardContent className="py-12 text-center text-slate-500">
              No tasks found. Create your first task!
            </CardContent>
          </Card>
        )}
      </div>

      {/* Task Dialog - EDIT FUNCTIONALITY */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-cabinet">{editingTask ? 'Edit Task' : 'Add Task'}</DialogTitle>
            <DialogDescription>
              {editingTask ? 'Make changes to your task below' : 'Fill in the task details below'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="title">Title *</Label>
              <Input 
                id="title"
                data-testid="task-title-input"
                value={formData.title} 
                onChange={(e) => setFormData({...formData, title: e.target.value})} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea 
                id="description"
                data-testid="task-description-input"
                value={formData.description} 
                onChange={(e) => setFormData({...formData, description: e.target.value})} 
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Status</Label>
                <Select value={formData.status} onValueChange={(v) => setFormData({...formData, status: v})}>
                  <SelectTrigger data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todo">To Do</SelectItem>
                    <SelectItem value="in_progress">In Progress</SelectItem>
                    <SelectItem value="completed">Completed</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="priority">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({...formData, priority: v})}>
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                    <SelectItem value="urgent">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="due_date">Due Date</Label>
              <Input 
                id="due_date"
                type="date"
                data-testid="task-due-date-input"
                value={formData.due_date} 
                onChange={(e) => setFormData({...formData, due_date: e.target.value})} 
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact">Related Contact</Label>
              <Select value={formData.contact_id} onValueChange={(v) => setFormData({...formData, contact_id: v})}>
                <SelectTrigger data-testid="task-contact-select">
                  <SelectValue placeholder="Select contact" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {contacts.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deal">Related Deal</Label>
              <Select value={formData.deal_id} onValueChange={(v) => setFormData({...formData, deal_id: v})}>
                <SelectTrigger data-testid="task-deal-select">
                  <SelectValue placeholder="Select deal" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {deals.map(d => (
                    <SelectItem key={d.id} value={d.id}>{d.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button 
              data-testid="save-task-btn"
              onClick={handleSubmit} 
              className="bg-royal-blue-700 hover:bg-royal-blue-800"
            >
              <Save size={16} className="mr-2" /> {editingTask ? 'Update Task' : 'Create Task'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

